import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ExtraErrorData } from '@sentry/integrations';
import posthog from 'posthog-js';
import { ApolloProvider } from '@apollo/client';
import { Toaster, toast } from 'react-hot-toast';
import TagManager from 'react-gtm-module';
import Cookies from 'universal-cookie';
import { i18n } from '@spacefill/shared/src/i18n/I18n';
import { UserProvider } from '@spacefill/shared/src/utils/UserContext';
import { BeamerProvider } from '@spacefill/shared/src/utils/Beamer';
import { NotificationProvider } from '@spacefill/shared/src/utils/NotificationContext';
import { ThemeStateProvider } from '@spacefill/shared/src/utils/ThemeStateContext';
import { overrideConsoleErrorToAddSentryCapture } from '@spacefill/shared/src/utils/OverrideConsole';
import { FeatureTogglingProvider, FeatureTogglingToolbar } from '@spacefill/shared/src/utils/FeatureToggling';
import { TodoTasksStateProvider } from '@spacefill/shared/src/utils/TodoTasksStateContext';
import { WarehouseNamingContextProvider } from '@spacefill/shared/src/utils/WarehouseNamingContext';
import { SpacefillThemeProvider } from '@spacefill/shared/src/components/Theme';
import { client as ApolloClient, pingGraphQLServer } from '@spacefill/shared/src/utils/Apollo';
import '@spacefill/shared/src/utils/YupMethods';

import { featureSettings } from './Features';
import { RouterContext } from './router/RouterContext';

import '@spacefill/uikit/src/index.css';

const cookies = new Cookies();
const sentryDSN = (typeof window !== 'undefined' && window?._spacefill_env_?.sentryDsn) || import.meta.env.VITE_EXTERNAL_CONSOLE_SENTRY_DSN;
const sentryEnvironment = (typeof window !== 'undefined' && window?._spacefill_env_?.sentryEnvironment) || import.meta.env.VITE_EXTERNAL_CONSOLE_SENTRY_ENVIRONMENT;
const googletagManagerId = (typeof window !== 'undefined' && window?._spacefill_env_?.googleTagManagerId) || import.meta.env.VITE_EXTERNAL_CONSOLE_GOOGLE_TAG_MANAGER_ID;
const googleTagManagerAuth = (typeof window !== 'undefined' && window?._spacefill_env_?.googleTagManagerAuth) || import.meta.env.VITE_EXTERNAL_CONSOLE_GOOGLE_TAG_MANAGER_AUTH;
const googletagManagerEnv = (typeof window !== 'undefined' && window?._spacefill_env_?.googleTagManagerEnv) || import.meta.env.VITE_EXTERNAL_CONSOLE_GOOGLE_TAG_MANAGER_ENV;
const postHogAPIKey = (typeof window !== 'undefined' && window?._spacefill_env_?.posthogApiKey) || import.meta.env.VITE_EXTERNAL_CONSOLE_POSTHOG_API_KEY;
const postHogAPIHost = (typeof window !== 'undefined' && window?._spacefill_env_?.posthogApiHost) || import.meta.env.VITE_EXTERNAL_CONSOLE_POSTHOG_API_HOST;

if (typeof sentryDSN === 'string' && sentryDSN !== '') {
    console.info('Sentry enable');
    Sentry.init({
        dsn: sentryDSN,
        release: import.meta.env.VITE_EXTERNAL_CONSOLE_SENTRY_RELEASE,
        ignoreErrors: ['ResizeObserver loop limit exceeded'],
        integrations: [
            new ExtraErrorData({
                depth: 11
            }),
            new Integrations.BrowserTracing()
        ],
        tracesSampleRate: 0.5,
        normalizeDepth: 11,
        environment: sentryEnvironment || 'unknown'
    });
    overrideConsoleErrorToAddSentryCapture();
}

if (typeof postHogAPIKey === 'string' && postHogAPIKey !== '') {
    posthog.init(postHogAPIKey, { api_host: postHogAPIHost });
}

if (
    googletagManagerId &&
    googletagManagerId !== ''
) {
    TagManager.initialize({
        gtmId: googletagManagerId,
        auth: googleTagManagerAuth,
        preview: googletagManagerEnv
    });
}

const enableMockTesting = new URLSearchParams(window.location.search).get('enableMockTesting');
if (enableMockTesting) {
    localStorage.setItem('enableMockTesting', '1');
}

if (window.navigator.userAgent === 'puppeteer') {
    window.toast = toast;
}

const App = () => {
    // Need to put it here because Apollo rerender at login and when in
    // impersonate mode it remove cookies before user is connected
    if (new URL(window.location.href).searchParams.get('impersonateUserId')) {
        cookies.remove('jwtTokenExternalConsole', {
            domain: document.domain,
            path: '/'
        });
        cookies.remove('jwtTokenExternalConsoleImpersonator', {
            domain: document.domain,
            path: '/'
        });
        cookies.remove('jwtTokenRememberMe', {
            domain: document.domain,
            path: '/'
        });
    }

    return (
        <ApolloProvider client={ApolloClient}>
            <NotificationProvider>
                <UserProvider>
                    <BeamerProvider>
                        <FeatureTogglingProvider featureSettings={featureSettings}>
                            <ThemeStateProvider>
                                <TodoTasksStateProvider>
                                    <SpacefillThemeProvider>
                                        <WarehouseNamingContextProvider>
                                            <RouterContext />
                                            <FeatureTogglingToolbar />
                                            <Toaster />
                                        </WarehouseNamingContextProvider>
                                    </SpacefillThemeProvider>
                                </TodoTasksStateProvider>
                            </ThemeStateProvider>
                        </FeatureTogglingProvider>
                    </BeamerProvider>
                </UserProvider>
            </NotificationProvider>
        </ApolloProvider>
    );
};

pingGraphQLServer((error) => {
    toast.error(i18n.t('Internal error!'));
    console.error(error);
});

ReactDOM.render(<App />, document.getElementById('root'));
